<template>
    <tbody>
        <AddRowHeader
            v-if="!staffMember.user_id"
            :save-disabled="saveDisabled"
            :save-click="saveClick"
            :close-click="closeClick"
        />
        <StaffRowHeader
            v-else
            :staff-member="staffMember"
            :editing="staffMember.editing"
            :is-form-active="isFormActive"
            :save-disabled="saveDisabled"
            :edit-click="editClick"
            :save-click="saveClick"
            :close-click="closeClick"
            :remove-click="removeClick"
            :undo-click="undoClick"
        />
        <AddEditStaffForm
            v-if="staffMember.editing"
            ref="add-edit-staff-form"
            :staff-member="editedStaffMember"
            :access-module-options="accessModuleOptions"
            :all-staff-members="allStaffMembers"
            :all-staff-groups="allStaffGroups"
            :message-group-options="numberOptions"
            @edit-another-staff="staff => editAnotherStaff(staff)"
            @scroll-to-edited-staff="$emit('scroll-to-edited-staff', editedStaffMember)"
        />
    </tbody>
</template>

<script type="text/babel">
import { mapState } from 'vuex';
import AddRowHeader from './AddRowHeader';
import StaffRowHeader from './StaffRowHeader';
import AddEditStaffForm from './AddEditStaffForm';
import { getEmptyStaffMember } from './staffUtils';
import mappingState from '../common/mappingState';
import cloneDeep from 'lodash-es/cloneDeep';
import { USER_TYPES } from '../../constants';

export default {
    name: 'StaffRow',

    components: {
        AddRowHeader,
        AddEditStaffForm,
        StaffRowHeader,
    },

    mixins: [mappingState],

    constants: {},

    props: {
        staffMember: {
            type: Object,
            default: () => ({
                // new staff
                ...getEmptyStaffMember(),
                editing: true,
            }),
        },

        accessModuleOptions: {
            type: Array,
            required: true,
        },
        // TODO is it possible to move allStaffMembers to staffUtils?
        // And don't reload data when user goes to StaffGroups
        allStaffMembers: {
            type: Array,
            required: true,
        },

        // TODO is it possible to move allStaffGroups to staffUtils?
        // And don't reload data when user goes to StaffGroups
        allStaffGroups: {
            type: Array,
            required: true,
        },

        messageGroupOptions: {
            type: Array,
            required: true,
        },

        portableDeviceOptions: {
            type: Array,
            required: true,
        },

        // using when another staff is editing
        isFormActive: {
            type: Boolean,
            default: false,
        },

        onClose: {
            type: Function,
            default: () => {},
        },

        onSave: {
            type: Function,
            default: () => {},
        },

        onRemove: {
            type: Function,
            default: () => {},
        },

        onEditStaff: {
            type: Function,
            default: () => {},
        },

        onEditAnotherStaff: {
            type: Function,
            default: () => {},
        },
    },

    data() {
        return {
            editedStaffMember: cloneDeep(this.staffMember),
        };
    },

    computed: {
        ...mapState(['unsavedChanges', 'isFormValid']),

        saveDisabled() {
            if (!this.staffMember.isAAPrimary) {
                if (this.editedStaffMember.accessModules.length === 0) {
                    return true;
                }
            }

            return !this.unsavedChanges || !this.isFormValid;
        },

        numberOptions() {
            if (this.staffMember.type === USER_TYPES.SUPER_USER) {
                return this.portableDeviceOptions;
            }

            return this.messageGroupOptions;
        },
    },

    created() {
        if (this.userType === USER_TYPES.SYS_ADMIN) {
            this.staffMember.type = USER_TYPES.SUPER_USER;
            this.editedStaffMember.type = USER_TYPES.SUPER_USER;
        }
    },

    methods: {
        editClick() {
            this.staffMember.editing = true;
            this.onEditStaff();
        },

        saveClick() {
            let { login_name, first_name, card_id, comment } = this.editedStaffMember;

            if (login_name) {
                this.editedStaffMember.login_name = login_name.trim();
            }
            if (first_name) {
                this.editedStaffMember.first_name = first_name.trim();
            }
            if (card_id) {
                this.editedStaffMember.card_id = card_id.trim();
            }
            if (comment) {
                this.editedStaffMember.comment = comment.trim();
            }
            if (this.$refs['add-edit-staff-form'].cardIdUsed) {
                this.confirmation({
                    confirmTitle: this.$t('staff.membersTab.wantSaveChanges'),
                    confirmBody:
                        this.$t('staff.membersTab.cardOccupied') +
                        '\n' +
                        this.$t('staff.membersTab.saveWarning'),
                    styleClass: 'saveConfirm',
                }).then(r => {
                    if (r) {
                        this.save();
                    }
                });
            } else {
                this.save();
            }
        },

        save() {
            this.onSave(this.editedStaffMember)
                .then(() => {
                    this.staffMember.editing = false;
                    this.staffMember.password = '';
                    this.staffMember.verifyPassword = '';
                    this.staffMember.loginIsChanged = false;
                    this.editedStaffMember = cloneDeep(this.staffMember);

                    this.$nextTick(() => {
                        // $nextTick is needed because editedStaffMember watcher calls setUnsavedChanges(true)
                        this.setUnsavedChanges(false);
                    });
                })
                .catch(() => {
                    this.staffMember.editing = true;
                    if (this.$refs['add-edit-staff-form']) {
                        this.$refs['add-edit-staff-form'].updateData();
                    }
                });
        },

        closeClick() {
            this.staffMember.editing = false;
            this.editedStaffMember = cloneDeep(this.staffMember);
            this.onClose();
        },

        removeClick() {
            this.onRemove(this.editedStaffMember);
        },

        undoClick() {
            this.editedStaffMember = cloneDeep(this.staffMember);

            this.$nextTick(() => {
                // $nextTick is needed because editedStaffMember watcher calls setUnsavedChanges(true)
                this.setUnsavedChanges(false);
            });
        },

        editAnotherStaff(staff) {
            this.staffMember.editing = false;
            this.editedStaffMember = cloneDeep(this.staffMember);
            this.onEditAnotherStaff(staff.user_id);
        },

        updateStaff() {
            this.editedStaffMember = cloneDeep(this.staffMember);
        },

        updateAssignedGroupNames(assignedGroupNames, isLast) {
            this.editedStaffMember.assignedGroupNames = assignedGroupNames;
            this.editedStaffMember.userIsLastAssignableInAssignedGroup = isLast;

            if (isLast) {
                this.editedStaffMember.assignable = true;
            }
        },
    },
};
</script>
